import type { ForeignExpense } from '@fin/shared';
import { useState } from 'react';
import { Button, ConfirmButton, Empty, ErrorBox, Field, Icon, Input, Loading, Modal, NumberInput, PageHead, Select, useAction } from '../../components/ui.tsx';
import { del, patch, post } from '../../lib/api.ts';
import { useCurrencies, useRates } from '../../lib/data.ts';
import { money, rub } from '../../lib/format.ts';
import { invalidate, useQuery } from '../../lib/query.ts';

/**
 * Spending in other currencies, as one list for a month. It stays out of periods, totals and reports:
 * the rouble estimate uses the rate of the latest transfer into that currency.
 */
export function ForeignPage() {
  const q = useQuery<ForeignExpense[]>('/foreign-expenses');
  const rates = useRates();
  const [edit, setEdit] = useState<ForeignExpense | 'new' | null>(null);
  if (q.error && !q.data) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q.data) return <Loading />;
  const items = q.data;
  const totals = [...items.reduce((m, i) => m.set(i.currency, (m.get(i.currency) ?? 0) + i.amount), new Map<string, number>())];
  const totalBase = totals.reduce((s, [cur, v]) => s + (rates.toBase(v, cur) ?? 0), 0);
  const unknownRate = totals.some(([cur]) => rates.toBase(1, cur) === null);

  return (
    <div className="page">
      <PageHead title="Траты в валюте" actions={<Button variant="primary" onClick={() => setEdit('new')}><Icon name="plus" /> Добавить</Button>} />
      {items.length ? (
        <section className="section foreign" aria-label="Траты в валюте за месяц">
          <div className="foreign-totals">
            <span className="muted small">В месяц</span>
            <div className="foreign-sums">
              {totals.map(([cur, v]) => <strong key={cur} className="money">{money(v, cur)}</strong>)}
            </div>
            {totalBase > 0 ? <span className="muted small">≈ <span className="money">{rub(Math.round(totalBase))}</span> по курсу последних переводов{unknownRate ? ', без валют без перевода' : ''}</span> : null}
          </div>
          <ul className="foreign-list">
            {items.map((i) => (
              <li key={i.id}>
                <button type="button" className="foreign-row" onClick={() => setEdit(i)}>
                  <span className="foreign-text">
                    <span className="foreign-name">{i.name}</span>
                    {i.note ? <span className="muted small">{i.note}</span> : null}
                  </span>
                  <span className="money">{money(i.amount, i.currency)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <Empty action={<Button variant="primary" onClick={() => setEdit('new')}><Icon name="plus" /> Добавить трату</Button>}>
          Списка пока нет. Сюда записываются ежемесячные траты в другой валюте — они не входят в периоды и итоги.
        </Empty>
      )}
      {edit ? <ForeignModal item={edit === 'new' ? undefined : edit} lastCurrency={items[items.length - 1]?.currency} onClose={() => setEdit(null)} /> : null}
    </div>
  );
}

function ForeignModal({ item, lastCurrency, onClose }: { item?: ForeignExpense; lastCurrency?: string; onClose: () => void }) {
  const currencies = useCurrencies();
  const [f, setF] = useState({ name: item?.name ?? '', amount: item?.amount ?? null as number | null, currency: item?.currency ?? lastCurrency ?? 'KZT', note: item?.note ?? '' });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const { busy, run } = useAction();
  const save = () => run(async () => {
    if (!f.name.trim()) throw new Error('Укажите название');
    const body = { ...f, amount: f.amount ?? 0 };
    if (item) await patch(`/foreign-expenses/${item.id}`, body); else await post('/foreign-expenses', body);
    invalidate('/foreign-expenses');
    onClose();
  }, 'Сохранено');
  return (
    <Modal className="modal-form" title={item ? item.name : 'Новая трата в валюте'} onClose={onClose} footer={<>
      {item ? <ConfirmButton size="md" onConfirm={async () => { await del(`/foreign-expenses/${item.id}`); invalidate('/foreign-expenses'); onClose(); }}>Удалить</ConfirmButton> : <span />}
      <div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Название"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus required /></Field>
        <div className="form-row">
          <Field label="Сумма в месяц" className="grow"><NumberInput value={f.amount} onChange={(v) => set({ amount: v })} /></Field>
          <Field label="Валюта">
            <Select value={f.currency} onChange={(e) => set({ currency: e.target.value })}>
              {(currencies.data ?? [{ code: f.currency }]).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Заметка"><Input value={f.note} onChange={(e) => set({ note: e.target.value })} /></Field>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
